import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebaseConfig";

// IMPORTANT: Importer les fonctions de validation que vous avez créées
import { 
  checkEmailExists, 
  createAccountWithValidation 
} from "../utils/accountValidation";

const CreateAccount = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "",
    company: "",
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [currentUser] = useAuthState(auth);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // 🔍 Récupérer le rôle de l'utilisateur connecté
  useEffect(() => {
    const getCurrentUserRole = async () => {
      if (currentUser) {
        try {
          const token = await currentUser.getIdTokenResult();
          setUserRole(token.claims.role);
        } catch (error) {
          console.error("Erreur récupération rôle:", error);
        }
      }
    };
    getCurrentUserRole();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // 🎯 Déterminer si l'utilisateur connecté peut approuver directement
  const canAutoApprove = () => {
    return userRole && (
      userRole.toLowerCase().includes('admin') || 
      userRole.toLowerCase().includes('super admin') ||
      userRole.toLowerCase().includes('fit')
    );
  };

  // 📝 Messages personnalisés selon le contexte
  const getProcessDescription = () => {
    if (canAutoApprove()) {
      return "En tant qu'administrateur FIT, vous pouvez créer des comptes directement. Les comptes seront activés immédiatement.";
    }
    return "Le compte sera créé en attente de validation. Un administrateur FIT devra l'approuver avant que l'utilisateur puisse se connecter.";
  };

  const getSuccessMessage = () => {
    if (canAutoApprove()) {
      return "Compte créé avec succès ! L'utilisateur peut maintenant se connecter immédiatement.";
    }
    return "Votre compte a été créé avec succès ! Il est en attente de validation par un administrateur.";
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsLoading(true);

    const { email, password, role, company } = formData;

    // Validation des champs
    if (!email || !password || !role) {
      setError("Tous les champs obligatoires doivent être remplis.");
      setIsLoading(false);
      return;
    }

    // Vérification de la longueur du mot de passe
    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      setIsLoading(false);
      return;
    }

    try {
      console.log("✅ Début création compte via API backend");

      // 🔧 Déterminer le statut d'approbation
      const isApproved = canAutoApprove();
      
      // Préparer les données utilisateur
      const userData = {
        email: email.toLowerCase(),
        role,
        company: company || "",
        isApproved, // ✅ Approuvé automatiquement si admin FIT
        createdBy: currentUser?.email || "system",
        approvedBy: isApproved ? currentUser?.email : null,
        approvedAt: isApproved ? new Date().toISOString() : null,
      };

      // ✅ UTILISER LA FONCTION DE VALIDATION (via API backend)
      const result = await createAccountWithValidation(email, password, userData);

      if (result.success) {
        console.log("✅ Compte créé avec succès via API");
        
        // Définir les revendications utilisateur via le backend
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/custom-claims/setCustomClaims`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                uid: result.userId,
                role: role,
                isApproved,
              }),
            }
          );

          const claimsResult = await response.json();
          console.log("✅ Claims définies:", claimsResult);

          if (response.ok) {
            setMessage(getSuccessMessage());
            
            // 🔧 Redirection adaptée selon le rôle
            const redirectDelay = canAutoApprove() ? 2000 : 3000;
            const redirectPath = canAutoApprove() ? "/fit/users" : "/";
            
            setTimeout(() => navigate(redirectPath), redirectDelay);
          } else {
            console.warn("Erreur claims, mais compte créé");
            setMessage("Compte créé mais erreur lors de la définition des permissions.");
          }
        } catch (fetchError) {
          console.warn("Backend claims non disponible, compte créé");
          setMessage(getSuccessMessage());
          setTimeout(() => navigate(canAutoApprove() ? "/fit/users" : "/"), 2000);
        }

      } else {
        // Gestion des différents types d'erreurs
        switch (result.type) {
          case "APPROVED_ACCOUNT_EXISTS":
          case "PENDING_ACCOUNT_EXISTS":
            setError(result.message);
            break;
          case "EMAIL_IN_USE":
            setError("Cet email est déjà utilisé.");
            break;
          case "WEAK_PASSWORD":
            setError("Le mot de passe est trop faible. Minimum 6 caractères.");
            break;
          case "INVALID_EMAIL":
            setError("Format d'email invalide.");
            break;
          default:
            setError(result.message || "Une erreur est survenue lors de la création du compte.");
        }
      }

    } catch (err) {
      console.error("❌ Erreur création compte:", err);
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex sm:flex-col md:flex-row min-h-screen">
      {/* Section gauche : Formulaire */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
          <h1 className='color-white'>Veryfit</h1>
          <h2 className="text-xl md:text-2xl font-bold text-customBlue">
              {canAutoApprove() ? "CRÉATION DE COMPTE UTILISATEUR" : "CRÉATION DE COMPTE"}
            </h2>
            <p className="text-gray-500 text-center mb-4">
              {canAutoApprove() 
                ? "Créer un nouveau compte utilisateur" 
                : "Veuillez remplir le formulaire pour demander un accès"
              }
            </p>
          </div>

          {/* 🔔 Message d'information sur le processus */}
          <div className={`mb-6 p-4 rounded-lg ${
            canAutoApprove() 
              ? "bg-green-50 border border-green-200" 
              : "bg-blue-50 border border-blue-200"
          }`}>
            <div className="flex items-start gap-3">
              <div className={`text-lg mt-0.5 ${
                canAutoApprove() ? "text-green-600" : "text-blue-600"
              }`}>
                {canAutoApprove() ? "✅" : "ℹ️"}
              </div>
              <div>
                <h4 className={`font-semibold mb-1 ${
                  canAutoApprove() ? "text-green-800" : "text-blue-800"
                }`}>
                  {canAutoApprove() ? "Création immédiate" : "Processus de création"}
                </h4>
                <p className={`text-sm ${
                  canAutoApprove() ? "text-green-700" : "text-blue-700"
                }`}>
                  {getProcessDescription()}
                </p>
              </div>
            </div>
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSignup}>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Adresse email*
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-300 ease-in-out"
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                Mot de passe*
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-300 ease-in-out"
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="role" className="block text-sm font-medium mb-2">
                Rôle*
              </label>
              <div className="mb-4">
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                  disabled={isLoading}
                >
                  <option value="">Sélectionner un rôle</option>
                  {canAutoApprove() && (
                    <>
                      <option value="Super Admin">Super Admin</option>
                      <option value="Admin">Admin</option>
                    </>
                  )}
                  <option value="Revendeur">Revendeur</option>
                  <option value="Carrossier">Carrossier</option>
                  <option value="Utilisateur">Utilisateur</option>
                </select>
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="company" className="block text-sm font-medium mb-2">
                Raison sociale
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder={canAutoApprove() ? "ex: FIT Doors, Client XYZ..." : "Nom de votre société"}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-300 ease-in-out"
                disabled={isLoading}
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full font-bold py-2 rounded-sm transition duration-300 ease-in-out transform ${
                isLoading 
                  ? "bg-gray-400 cursor-not-allowed" 
                  : "bg-red-600 text-white hover:bg-red-700 hover:scale-105"
              }`}
            >
              {isLoading 
                ? "Création en cours..." 
                : (canAutoApprove() ? "Créer le compte" : "Créer un compte")
              }
            </button>
          </form>
          
          {message && (
            <p className="mt-4 text-green-500 text-center">{message}</p>
          )}
          {error && <p className="mt-4 text-red-500 text-center">{error}</p>}
          
          {/* 🔙 Bouton retour pour les admins */}
          {canAutoApprove() && (
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                disabled={isLoading}
              >
                ← Retour au tableau de bord
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Section droite : Visuel */}
      <div className="hidden md:flex w-full md:w-1/2 bg-login bg-cover bg-center relative">
        <div className="z-10 text-white p-16 flex justify-start flex-col items-start animate-fade-in">
          <h2 className="text-2xl md:text-3xl font-extrabold leading-tight tracking-wide animate-text-glow">
            {canAutoApprove() 
              ? "GESTION DES UTILISATEURS" 
              : "PLATEFORME DE GESTION"
            }
            <br /> 
            {canAutoApprove() 
              ? "ADMINISTRATION FIT" 
              : "DES COMMANDES"
            }
          </h2>
          <div className="mt-8 w-[8rem] md:w-[10rem] h-[8px] md:h-[10px] bg-red-600 animate-grow"></div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;